/**
 * JobDocumentsCard — the job's files (JOB-DOCS-01 → 02 → FILES-AI-01 / FILES-JOB-01).
 *
 * Seth 2026-08-18: "Where do we upload documents for this job like heat load
 * calcs, warranty certifications..."; 2026-09-24: crews "review any documents
 * and pictures etc … Plans, load calcs, pictures, etc. Scope of job";
 * 2026-09-28: "can AI identify the category and suggest when uploading rather
 * than having to specify. Also if a tech uploads pictures and docs during the
 * service/sales process will they show up in documents in correct category?
 * Will there be an easy way to download, edit, markup, rename, etc from the
 * office?"
 *
 *   · Upload (or drop files on the card) — no type to pick first: the AI reads
 *     each file and files it (classify-file). Anything with prices goes to
 *     Office Only. The type stays one click to change.
 *   · Everything taken on this job's calls lands here by itself — the call it
 *     was sold on and every visit on it (the database pins those files to the
 *     job) — photos from the phone included, sorted the same way.
 *   · Photos show as pictures; documents as a list. Click any file for the
 *     viewer: rename, type, crew switch, mark up, new version, download,
 *     email / text, remove.
 *
 *   · Download All (FILES-BULK-01): every file on the job in one zip, a folder
 *     per document type.
 *
 * One file system: uploads pin to the job AND keep the custno, so the same
 * heat-load calc shows here and on the customer's 360 Files. Every file has a
 * TYPE; each type says whether the crew's phone shows it (Office Only keeps
 * contracts / builder POs — prices — off the phone), and the eye flips any
 * one file. Archive, never delete.
 */
import { Fragment, useEffect, useRef, useState, type DragEvent } from "react";
import { Download, Eye, EyeOff, FileText, FolderOpen, Loader2, Plus, Sparkles, Upload } from "lucide-react";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { toastError } from "@/components/ui/mutationToast";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import {
  crewCanSee, downloadFile, fileWhen, fileWho, isImageFile, jobDocTypeOf, prettySize, useJobDocTypes, useJobFiles,
  useSetFileCrewVisible, useSetFileDocTag, useSignedUrls, useSortFiles, useUploadFiles, JOB_DOC_TYPES_DEFAULT,
  type PegasusFile,
} from "@/features/files/filesCore";
import { ArchiveFileButton } from "@/features/files/ArchiveFileButton";
import { FilePreviewModal } from "@/features/files/FilePreviewModal";
import { downloadZip, zipTitle } from "@/features/files/zip";
import type { Job } from "./jobs";

/** A file the AI hasn't read yet and nobody typed — it's being sorted (or waits for the sweep). */
function isSorting(f: PegasusFile, busy: Set<string>): boolean {
  if (f.ai_at || f.doc_tag) return false;
  return busy.has(f.id) || Date.now() - new Date(f.created_at).getTime() < 3 * 60 * 1000;
}

export function JobDocumentsCard({ job }: { job: Job }) {
  const { data: files } = useJobFiles(job.id);
  // A photo from the phone, or the AI's pick, shows up without a refresh.
  useRealtimeRefresh(["pegasus_file"], [["files"], ["dispatch_files"], ["customer_files"]]);
  const list = files ?? [];
  const thumbs = useSignedUrls(list.filter((f) => isImageFile(f))).data;
  const { data: typesQ } = useJobDocTypes();
  const types = typesQ ?? JOB_DOC_TYPES_DEFAULT;
  const upload = useUploadFiles({ custno: job.custno, locno: job.locno ?? null, job_id: job.id });
  const sort = useSortFiles();
  const setVis = useSetFileCrewVisible();
  const setTag = useSetFileDocTag();
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<PegasusFile | null>(null);
  const [drag, setDrag] = useState(false);
  const pick = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [zipping, setZipping] = useState<string | null>(null);

  // The sweep: files that reached the job without a type and were never read
  // (the sale call's photos, an older phone, the portal) get sorted — a few at
  // a time, once per visit to the page.
  const tried = useRef(new Set<string>());
  useEffect(() => {
    const todo = (files ?? []).filter((f) => !f.ai_at && !f.doc_tag && !tried.current.has(f.id)).map((f) => f.id).slice(0, 8);
    if (!todo.length) return;
    todo.forEach((id) => tried.current.add(id));
    setBusy((b) => new Set([...b, ...todo]));
    sort.mutate({ ids: todo }, { onSettled: () => setBusy((b) => { const n = new Set(b); todo.forEach((id) => n.delete(id)); return n; }) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const onFiles = (picked: File[]) => {
    if (!picked.length) return;
    setNotice(null);
    upload.mutate(picked, {
      onSuccess: (r) => setNotice(r.skipped.length ? `Skipped: ${r.skipped.join(", ")}` : null),
      onError: (e) => setNotice(e instanceof Error ? `Upload failed: ${e.message}` : "Upload failed."),
    });
  };
  const onDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDrag(false);
    onFiles([...(e.dataTransfer?.files ?? [])]);
  };

  // Group in the type order Settings defines; pictures first inside a type, newest first.
  // New uploads the AI is still reading sit on top under "Sorting…", then move to their shelf.
  const SORTING = "__sorting";
  const order = new Map<string, number>([[SORTING, -1], ...types.map((t, i) => [t.key, i] as [string, number])]);
  const rows = list.map((f) => ({ f, type: isSorting(f, busy) ? SORTING : jobDocTypeOf(f, types), crew: crewCanSee(f, types), pic: isImageFile(f) }))
    .sort((a, b) => (order.get(a.type) ?? 99) - (order.get(b.type) ?? 99) || Number(b.pic) - Number(a.pic) || b.f.created_at.localeCompare(a.f.created_at));
  const groups: { type: string; items: typeof rows }[] = [];
  for (const r of rows) {
    const g = groups[groups.length - 1];
    if (g && g.type === r.type) g.items.push(r); else groups.push({ type: r.type, items: [r] });
  }
  const crewCount = rows.filter((r) => r.crew).length;
  const typeLabel = (k: string) => (k === SORTING ? "Sorting…" : types.find((t) => t.key === k)?.label ?? k);
  // FILES-BULK-01: the whole job in one zip, a folder per type.
  const zipAll = async () => {
    setZipping("…");
    try {
      const r = await downloadZip(rows.map((x) => x.f), `${zipTitle(job.address ?? job.customer_name)} — job files`,
        (f) => `${(typeLabel(jobDocTypeOf(f, types)) || "Files").replace(/[\\/]/g, "-")}/${f.filename}`,
        (d, t) => setZipping(`${d} of ${t}`));
      if (r.skipped.length) setNotice(`Left out (wouldn't download): ${r.skipped.join(", ")}`);
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Couldn't make the zip.");
    } finally {
      setZipping(null);
    }
  };

  return (
    <section
      className={cn("relative rounded-xl border bg-white p-4 transition-colors", drag ? "border-brand-400 ring-2 ring-brand-200" : "border-hairline")}
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes("Files")) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDrag(false); }}
      onDrop={onDrop}
    >
      <h3 className="mb-1 flex flex-wrap items-center gap-2 text-base font-semibold text-slate-800">
        <FolderOpen className="h-[18px] w-[18px] text-brand-500" /> Documents
        <InfoTip label="About job documents">
          Plans, load calcs, scope, site photos, spec sheets, permit paperwork. Upload or drop files here — the AI reads each
          one and files it under its type (anything showing prices goes to Office Only); change the type if it guessed wrong.
          Photos and files the techs take on this job&apos;s calls land here too. Click a file to rename, mark up, upload a new
          version or download it. The eye shows or hides a file on the crew&apos;s phone.
        </InfoTip>
        <span className="ml-auto flex items-center gap-1.5">
          {rows.length > 1 && (
            <button type="button" onClick={() => void zipAll()} disabled={!!zipping} title="Every file on this job in one zip, a folder per type"
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:opacity-60">
              {zipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {zipping ? `Zipping ${zipping}` : "Download All"}
            </button>
          )}
          <button type="button" onClick={() => pick.current?.click()} disabled={upload.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-50 px-3 py-1.5 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-60">
            {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {upload.isPending ? "Uploading…" : "Upload"}
          </button>
          <input ref={pick} type="file" multiple className="hidden" aria-label="Upload files to this job"
            onChange={(e) => { const l = e.target.files ? [...e.target.files] : []; e.target.value = ""; onFiles(l); }} />
        </span>
      </h3>
      {rows.length > 0 && (
        <p className="mb-2 text-sm text-slate-500">The crew&apos;s phone shows <b className="font-semibold text-slate-700">{crewCount} of {rows.length}</b></p>
      )}
      {notice && <p className="mb-2 text-sm font-medium text-amber-700">{notice}</p>}

      {rows.length === 0 ? (
        <button type="button" onClick={() => pick.current?.click()}
          className="flex w-full flex-col items-center gap-1 rounded-lg border border-dashed border-hairline py-6 text-center text-sm text-slate-500 transition-colors hover:border-brand-300 hover:bg-brand-50/40">
          <Upload className="h-5 w-5 text-slate-400" />
          Nothing on file yet — drop files here or click to upload.
        </button>
      ) : (
        <div className="space-y-1">
          {groups.map((g) => {
            const pics = g.items.filter((r) => r.pic);
            const docs = g.items.filter((r) => !r.pic);
            return (
              <Fragment key={g.type}>
                <div className="pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                  {typeLabel(g.type)} <span className="font-semibold text-slate-300">· {g.items.length}</span>
                </div>
                {pics.length > 0 && (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-2 pb-1">
                    {pics.map(({ f, crew }) => (
                      <div key={f.id} className="group relative">
                        <button type="button" onClick={() => setPreview(f)} title={f.ai_summary ?? f.caption ?? f.filename}
                          className="block aspect-square w-full overflow-hidden rounded-lg bg-slate-100 ring-1 ring-inset ring-hairline transition hover:ring-2 hover:ring-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                          {thumbs?.get(f.storage_path)
                            ? <img src={thumbs.get(f.storage_path)} alt={f.ai_summary ?? f.caption ?? f.filename} loading="lazy" className="h-full w-full object-cover" />
                            : <span className="block h-full w-full animate-pulse bg-slate-100" />}
                        </button>
                        <span className="pointer-events-none absolute bottom-1 left-1 flex items-center gap-1">
                          {isSorting(f, busy) && <span className="rounded-full bg-white/90 p-0.5 text-violet-600 shadow-sm"><Loader2 className="h-3 w-3 animate-spin" /></span>}
                          {!crew && <span className="rounded-full bg-white/90 p-0.5 text-slate-500 shadow-sm" title="Office only"><EyeOff className="h-3 w-3" /></span>}
                        </span>
                        <ArchiveFileButton fileId={f.id} filename={f.filename} look="overlay" />
                      </div>
                    ))}
                  </div>
                )}
                {docs.length > 0 && (
                  <ul className="divide-y divide-slate-100">
                    {docs.map(({ f, type, crew }) => (
                      <li key={f.id} className="group flex items-center gap-2.5 py-1.5">
                        <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                        <button type="button" onClick={() => setPreview(f)} className="min-w-0 flex-1 text-left">
                          <span className="block truncate text-sm font-medium text-slate-700 hover:text-brand-700 hover:underline">{f.filename}</span>
                          <span className="block truncate text-xs text-slate-500">
                            {[f.ai_summary, `${fileWho(f)} · ${fileWhen(f.created_at)}`, prettySize(f.size_bytes)].filter(Boolean).join(" · ")}
                          </span>
                        </button>
                        {isSorting(f, busy) ? (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">
                            <Loader2 className="h-3 w-3 animate-spin" /> Sorting…
                          </span>
                        ) : (
                          <span className="inline-flex shrink-0 items-center gap-0.5">
                            {f.tag_by === "ai" && <span title="Sorted by AI — change it if it's wrong"><Sparkles className="h-3.5 w-3.5 text-violet-500" /></span>}
                            <select value={type} aria-label={`Type of ${f.filename}`} disabled={setTag.isPending}
                              onChange={(e) => setTag.mutate({ id: f.id, doc_tag: e.target.value })}
                              className="max-w-[9rem] rounded border border-transparent bg-transparent py-0.5 text-xs text-slate-500 hover:border-hairline focus:border-brand-500 focus:outline-none">
                              {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </select>
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => setVis.mutate({ id: f.id, crew_visible: !crew })}
                          title={crew ? "The crew can see this on their phone — click to make it office only" : "Office only — click to let the crew see it"}
                          aria-label={crew ? "Visible to the crew" : "Office only"}
                          className={cn("rounded p-1 transition-colors", crew ? "text-brand-600 hover:bg-brand-50" : "text-slate-300 hover:bg-slate-100 hover:text-slate-500")}
                        >
                          {crew ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        </button>
                        <button type="button" title="Download" aria-label={`Download ${f.filename}`}
                          onClick={() => void downloadFile(f).catch(() => toastError("Couldn't download it — try again."))}
                          className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
                          <Download className="h-3.5 w-3.5" />
                        </button>
                        {/* 360-AUDIT wave 2 (A): visible at rest + two-click confirm. */}
                        <ArchiveFileButton fileId={f.id} filename={f.filename} look="row" />
                      </li>
                    ))}
                  </ul>
                )}
              </Fragment>
            );
          })}
        </div>
      )}

      {drag && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-brand-50/80 text-sm font-semibold text-brand-800">
          <Upload className="mr-2 h-5 w-5" /> Drop to add to this job
        </div>
      )}
      <FilePreviewModal file={preview} custno={job.custno} onClose={() => setPreview(null)} />
    </section>
  );
}
